import { posthog } from "posthog-js";

import { auth$ } from "~/stores/auth";
import { activeOrganization$ } from "~/stores/organizations";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;

/**
 * True when PostHog has a project key configured and we're in a production
 * build. Mirrors the Sentry init gating in `sentry.lib.ts`.
 */
export const isPostHogEnabled = !!POSTHOG_KEY && import.meta.env.PROD;

// Side effects must stay client-only. `__root.tsx` imports this module on the
// server for the PostHogProvider reference, and server-side route handlers
// still load the router bundle — init must not run there.
if (typeof window !== "undefined" && isPostHogEnabled && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    // Route events through our own domain via the splat proxy at
    // `/api/m/$` to reduce ad-blocker loss. The path is intentionally
    // generic so it doesn't match common analytics blocklists.
    api_host: "/api/m",
    ui_host: import.meta.env.VITE_POSTHOG_UI_HOST ?? "https://us.posthog.com",
    // Opt into the latest PostHog defaults — most importantly,
    // `capture_pageview: 'history_change'` so $pageview fires on TanStack
    // Router navigations, not just initial page load.
    defaults: "2026-01-30",
    capture_pageview: "history_change",
    capture_pageleave: true,
    person_profiles: "identified_only",
    ...(import.meta.env.VITE_ENV
      ? { environment: import.meta.env.VITE_ENV }
      : {}),
  });

  auth$.onChange(
    ({ value }) => {
      const user = value?.user;
      if (user) {
        posthog.identify(user.id, {
          email: user.email ?? undefined,
          name: user.name ?? undefined,
        });
      } else {
        posthog.reset();
      }
    },
    { initial: true },
  );

  activeOrganization$.onChange(
    ({ value }) => {
      if (value?.id) {
        posthog.group("organization", value.id, {
          name: value.name ?? undefined,
        });
      }
    },
    { initial: true },
  );
}

/**
 * Capture a custom PostHog event. Safe to call regardless of whether
 * PostHog is enabled — calls before init are queued internally, and
 * when disabled this becomes a no-op.
 */
export const captureEvent = (
  name: string,
  properties?: Record<string, unknown>,
) => {
  if (!isPostHogEnabled) return;
  posthog.capture(name, properties);
};

export { posthog };
